
// ─── Types ────────────────────────────────────────────────────────────────────

import { UseMutationOptions, useMutation } from "react-query";

import { IDocumentModel } from "../../interfaces/ICase";

export interface IDocumentBaseRequestModel {
  entityGuid: string;
  entityTypeId: number;
  noteTypeId?: number | null;
  strategyTypeId?: number | null;
}

export interface IDocumentUploadRequestModel extends IDocumentBaseRequestModel {
  documents: IDocumentModel[];
  tenantGuid: string;
}

export interface IUploadDocumentResponse<T = unknown> {
  message: string;
  data: T;
}

export interface IUploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export interface IUseDocumentUploadOptions<T = unknown>
  extends Omit<
    UseMutationOptions<IUploadDocumentResponse<T>, Error, IDocumentUploadRequestModel>,
    "mutationFn"
  > {
  endpoint: string;
  onProgress?: (progress: IUploadProgress) => void;
}

// ─── FormData builder ─────────────────────────────────────────────────────────

/**
 * Builds the multipart/form-data payload expected by DocumentUploadRequestModel.
 *
 * - Scalar base fields are appended individually for [FromForm] binding
 * - "documents" is JSON-stringified so the server can deserialize via
 *   formCollection.TryGetValue("documents", ...)
 * - Each file is appended under "files" for IFormCollection binding
 */
function buildFormData(request: IDocumentUploadRequestModel): FormData {
  const formData = new FormData();

  formData.append("entityGuid", request.entityGuid);
  formData.append("entityTypeId", String(request.entityTypeId));

  if (request.noteTypeId != null) {
    formData.append("noteTypeId", String(request.noteTypeId));
  }
  if (request.strategyTypeId != null) {
    formData.append("strategyTypeId", String(request.strategyTypeId));
  }

  // Server reads this via formCollection.TryGetValue("documents", ...)
  formData.append("documents", JSON.stringify(request.documents));

  // Append each file — server binds these via IFormCollection
  request.documents
    .filter((doc) => doc.file != null)
    .forEach((doc) => {
      formData.append("files", doc.file as File);
    });

  return formData;
}

// ─── Fetch function ───────────────────────────────────────────────────────────

async function uploadDocuments<T>(
  request: IDocumentUploadRequestModel,
  endpoint: string,
  onProgress?: (progress: IUploadProgress) => void
): Promise<IUploadDocumentResponse<T>> {
  const formData = buildFormData(request);

  return new Promise<IUploadDocumentResponse<T>>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percent: Math.round((event.loaded / event.total) * 100),
          });
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as IUploadDocumentResponse<T>);
        } catch {
          reject(new Error("Failed to parse server response"));
        }
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Network error during upload")));
    xhr.addEventListener("abort", () => reject(new Error("Upload aborted")));

    xhr.open("POST", endpoint);

    // Do NOT set Content-Type — the browser sets multipart/form-data with the
    // correct boundary automatically when sending FormData via XHR.
    xhr.setRequestHeader("X-Tenant-Guid", request.tenantGuid);

    xhr.send(formData);
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @example
 * const { upload, isUploading } = useDocumentUpload({
 *   endpoint: props.uploadUrl,
 *   onProgress: ({ percent }) => setProgress(percent),
 *   onSuccess: ({ message, data }) => {
 *     if (message === "Success") {
 *       props.onSaveComplete(cloneDeep(data));
 *       showSuccessToast("Documents uploaded successfully");
 *     }
 *   },
 *   onError: () => showErrorToast("Error uploading documents"),
 * });
 *
 * upload({
 *   entityGuid: props.entityGuid,
 *   entityTypeId: props.entityTypeId,
 *   noteTypeId: props.noteTypeId,
 *   documents,
 *   tenantGuid: zoneGuid,
 * });
 */
export function useDocumentUpload<T = unknown>({
  endpoint,
  onProgress,
  ...mutationOptions
}: IUseDocumentUploadOptions<T>) {
  const mutation = useMutation<IUploadDocumentResponse<T>, Error, IDocumentUploadRequestModel>({
    mutationFn: (request) => uploadDocuments<T>(request, endpoint, onProgress),
    ...mutationOptions,
  });

  return {
    upload: mutation.mutate,
    uploadAsync: mutation.mutateAsync,
    isUploading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    data: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
}